import { OrderPriceType, ProcessingMode } from "../../../utils/Order/Order";
import { getOrderStats } from "../../../utils";
import { showToast } from "../../../utils/Helper";

export function calculateAvailableQty(order) {
  let orderStats = order.stats;
  if (!orderStats) {
    orderStats = getOrderStats(order);
  }

  let availableQty = +orderStats.pending;

  if (order.OrderQuantityIsCash) {
    availableQty = +order.OrderQuantity;
  }

  if (order.OrderLowTouchEnabled) {
    const { Childs } = order;

    availableQty = 0;

    if (Childs) {
      Childs.forEach((child) => {
        if (child.ChildProcessingMode == ProcessingMode.HighTouch) {
          availableQty += +child.ChildOrderQuantity;
          if (child.Executions) {
            child.Executions.forEach((execution) => {
              availableQty -= +execution.ExecutionQuantity;
            });
          }
        }
      });
    }
  }

  return availableQty;
}

export function initialize(order) {
  let cashQuantity = "";
  let availableQty = calculateAvailableQty(order);
  let orderPrice = "";
  let currentDate = new Date().toDatetimeLocal();

  if (order.OrderQuantityIsCash) {
    cashQuantity = +order.OrderQuantity;
    availableQty = "";
  }

  if (order.AssetClassForm === "Repo") {
    cashQuantity = +order.OrderQuantity;
    availableQty = +order.OrderQuantity;
  }

  if (order.OrderType === "Limit") {
    orderPrice = order.OrderPrice;
  }

  if (order.OrderType === "Stop") {
    orderPrice = order.OrderStopPrice;
  }

  this.state = {
    execution: {
      ExecutionDate: currentDate,
      ExecutionQuantity: availableQty,
      ExecutionCashQuantity: cashQuantity,
      ExecutionPrice: orderPrice,
      ExecutionFXRoute: "FXThroughCpty",
      ExecutionFX: "",
      CounterPartyId: null,
      CounterPartyName: "",
      ExecutionMarkupPrice: "",
    },
    errors: {},
    loading: false,
    tradeRoutes: null,
    availableQty,
    initialDate: currentDate,
  };
}

export function setTradeRoutes(tradeRoutes, tradeRoutesLogs) {
  this.setState({ tradeRoutes, tradeRoutesLogs });
}

export function changeValues(values, callback) {
  let newState = this.state;

  if (values.TradingRouteId !== -1) {
    Object.entries(values).forEach((entry) => {
      const [name, value] = entry;
      newState = {
        execution: {
          ...newState.execution,
          [name]: value,
        },
        errors: {
          ...newState.errors,
          [name]: undefined,
        },
      };
    });
    this.setState({ ...newState }, this.calculateTotal);
  }
}

export function clear(initValues) {
  this.setState({
    execution: {
      ExecutionDate: new Date().toDatetimeLocal(),
      ExecutionQuantity: "",
      ExecutionPrice: "",
      BrokerId: null,
      CounterPartyId: null,
      CounterPartyName: "",
      ExecutionMarkupPrice: "",
      ...initValues,
    },
    sharesQuantity: null,
    errors: {},
    loading: false,
  });
}

export function validate(order) {
  const { execution, availableQty } = this.state;

  let errors = {};

  if (execution.ExecutionPrice == "") {
    errors["ExecutionPrice"] = "Required field";
  } else if (order.OrderType == OrderPriceType.LIMIT || order.OrderType == OrderPriceType.STOPLIMIT) {
    if (order.OrderSide == "Buy" && +order.OrderPrice < +execution.ExecutionPrice) {
      if (order.AssetClassForm !== "Repo") {
        errors["ExecutionPrice"] = "Invalid price upper limit";
      }
    }
    if (order.OrderSide == "Sell" && +order.OrderPrice > +execution.ExecutionPrice) {
      if (order.AssetClassForm !== "Repo") {
        errors["ExecutionPrice"] = "Invalid price above limit";
      }
    }
  }

  if (order.AssetClassForm === "Repo") {
    if (!execution.contractdata.CollateralAssetTicker) {
      errors["CollateralAsset"] = "Required field";
    }

    if (+order.OrderPrice > 0 && +order.OrderPrice < +execution.contractdata.rate) {
      errors["CollateralRate"] = "Invalid rate above limit";
    }

    if (+execution.contractdata.CollateralQuantity === 0) {
      errors["CollateralQuantity"] = "Invalid quantity cannot be 0";
    }

    if (+execution.contractdata.appraisal > 100 || +execution.contractdata.appraisal < 0) {
      errors["CollateralAppraisal"] = "Invalid appraisal outside limits";
    }

    if (+execution.contractdata.margin > 100 || +execution.contractdata.margin < 0) {
      errors["CollateralHaircut"] = "Invalid haircut outside limits";
    }

    if (+execution.contractdata.appraisal + +execution.contractdata.margin !== 100) {
      errors["CollateralAppraisal"] = "Invalid haircut/appraisal balance";
      errors["CollateralHaircut"] = "Invalid haircut/appraisal balance";
    }

    if (execution.contractdata.maturity === undefined || execution.contractdata.maturity === null) {
      errors["CollateralMaturity"] = "Required field";
    }
  }

  if (execution.ExecutionQuantity == "") {
    errors["ExecutionQuantity"] = "Required field";
  } else if (+execution.ExecutionQuantity > +order.stats.pending) {
    if (!order.OrderQuantityIsCash) {
      errors["ExecutionQuantity"] = `Must be less or equal than ${order.stats.pending}`;
    }
    // has an execution
    else if (order.OrderQuantityIsCash && order.Childs && order.Childs.length > 0) {
      if (order.Childs[0].Executions.length > 0) {
        errors["ExecutionQuantity"] = `Already filled`;
      }
    }
  }

  // check only when lt enabled
  if (order.OrderLowTouchEnabled) {
    if (+execution.ExecutionQuantity > availableQty) {
      errors["ExecutionQuantity"] = `Must be less or equal than ${availableQty}`;
    }
  }

  if (order.AssetClassForm !== "Forex" && order.OrderCurrencySymbol !== order.OrderSettlementCurrencySymbol) {
    // The order trade currency is different from the settle currency, so the trader must indicate an FX.
    if (execution.ExecutionFXRoute == "") {
      errors["ExecutionFXRoute"] = "Required field";
    } else {
      if (
        execution.ExecutionFXRoute == "FXThroughCpty" &&
        (execution.ExecutionFX == "" || execution.ExecutionFX <= 0)
      ) {
        errors["ExecutionFX"] = "Required field";
      }
    }
  }

  if (isNaN(new Date(execution.ExecutionDate))) {
    errors["ExecutionDate"] = "Invalid value";
  }

  if (execution.TradingRouteId === -1) errors["TradingRouteId"] = "Required field";
  if (!execution.CounterPartyId) errors["CounterPartyId"] = "Required field";

  if (execution.ExecutionDate === undefined || execution.ExecutionDate === null) {
    errors.ExecutionDate = "Required field";
  }
  if (execution.ExecutionValueDate === undefined || execution.ExecutionValueDate === null) {
    errors.ExecutionValueDate = "Required field";
  }

  let ExecutionDateAux = new Date(execution.ExecutionDate);
  ExecutionDateAux = new Date(`${ExecutionDateAux.toDateLocal()}T00:00`);
  const ExecutionValueDateAux = new Date(`${execution.ExecutionValueDate}T00:00`);
  if (ExecutionDateAux > ExecutionValueDateAux) {
    errors.ExecutionDate = "Trade date should be before or at value date";
    errors.ExecutionValueDate = "Value date should be after or at trade date";
  }

  const { minTradeDate, maxTradeDate, restricted } = this.state;
  let ExecutionDateAux2 = new Date(execution.ExecutionDate);
  let minExecutionDate = new Date(minTradeDate);
  let maxExecutionDate = new Date(maxTradeDate);
  if (ExecutionDateAux2 < minExecutionDate || ExecutionDateAux2 > maxExecutionDate) {
    if (restricted) {
      errors.ExecutionDate = `Trade date outside allowed time range (${minExecutionDate.toLocaleTimeString()}-${maxExecutionDate.toLocaleTimeString()})`;
    } else {
      showToast(
        `The trade date was updated because it was outside allowed time range (${minExecutionDate.toLocaleTimeString()}-${maxExecutionDate.toLocaleTimeString()})`
      );
      let newExecutionDate = minTradeDate;
      if (ExecutionDateAux2 > maxExecutionDate) newExecutionDate = maxTradeDate;
      this.setState((state) => ({
        ...state,
        execution: {
          ...state.execution,
          ExecutionDate: newExecutionDate,
        },
      }));
    }
  }

  if (this.props.applyMarkupTrader && order.OrderType == "Limit") {
    const totalPrice = parseFloat(execution.ExecutionPrice) + parseFloat(execution.ExecutionMarkupPrice);
    const differencePrice = parseFloat(execution.ExecutionPrice) - parseFloat(execution.ExecutionMarkupPrice);

    if (order.OrderSide === "Buy" && totalPrice > order.OrderPrice) {
      errors.ExecutionMarkupPrice = "Markup price may not be higher than the limit price";
    } else if (order.OrderSide === "Sell" && differencePrice < order.OrderPrice) {
      errors.ExecutionMarkupPrice = "Markup price may not be lower than the limit price";
    }
  }
  this.setState({ errors });

  return !Object.keys(errors).length;
}
